/**
 * SUNTRONIX ONE — Tenant Onboarding Wizard.
 * Turns the 7-step SOP into a guided 5-step flow so ops can spin up a
 * new customer without leaving the app or opening curl.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Loader2, Plus, Trash2, ShieldCheck, Copy, Mail, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CUSTOMER_ROLES = [
  "customer_global_admin", "customer_it_admin", "customer_procurement",
  "customer_finance", "customer_hr", "customer_manager",
  "customer_employee", "customer_auditor", "customer_viewer",
];

export default function OnboardTenantWizard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [master, setMaster] = useState({ name: "", code: "", legal_name: "", country: "India", domains: "" });
  const [masterId, setMasterId] = useState(null);
  const [subs, setSubs] = useState([{ name: "", code: "", country: "India" }]);
  const [subIds, setSubIds] = useState([]);
  const [warehouse, setWarehouse] = useState({ name: "", code: "", country: "India" });
  const [warehouseId, setWarehouseId] = useState(null);
  const [users, setUsers] = useState([
    { email: "", role: "customer_global_admin" },
  ]);
  const [createdUsers, setCreatedUsers] = useState([]);

  if (user && user.role !== "super_admin") {
    return <div className="p-8 text-center text-slate-500 text-sm">Super admin only.</div>;
  }

  const steps = ["Master", "Subsidiaries", "Warehouse", "Users", "Review"];

  const submitMaster = async () => {
    if (!master.name.trim() || !master.code.trim()) {
      toast.error("Name and Code are required"); return;
    }
    const domainList = (master.domains || "")
      .split(/[,\s]+/).map((d) => d.trim().toLowerCase().replace(/^@/, ""))
      .filter(Boolean);
    setBusy(true);
    try {
      const { data } = await api.post("/master-companies", {
        name: master.name.trim(),
        code: master.code.trim().toUpperCase(),
        legal_name: (master.legal_name || master.name).trim(),
        country: master.country,
        domains: domainList,
      });
      setMasterId(data.id || data.master_company_id);
      toast.success(domainList.length
        ? `Created ${master.name} · ${domainList.length} domain(s) registered`
        : `Created ${master.name}`);
      setStep(1);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const submitSubs = async () => {
    setBusy(true);
    const created = [];
    for (const s of subs) {
      if (!s.name.trim() || !s.code.trim()) continue;
      try {
        const { data } = await api.post("/companies", {
          name: s.name.trim(),
          code: s.code.trim().toUpperCase(),
          country: s.country,
          master_company_id: masterId,
        });
        created.push(data.id);
      } catch (e) { toast.error(`${s.name}: ${e.response?.data?.detail || "failed"}`); }
    }
    setSubIds(created);
    setBusy(false);
    setStep(2);
  };

  const submitWarehouse = async () => {
    if (!warehouse.name.trim() || !warehouse.code.trim()) {
      toast.error("Warehouse name and code are required"); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/warehouses", {
        name: warehouse.name.trim(),
        code: warehouse.code.trim().toUpperCase(),
        country: warehouse.country,
        master_company_id: masterId,
        kind: "primary", active: true,
      });
      setWarehouseId(data.id);
      toast.success(`Warehouse ${warehouse.name} ready`);
      setStep(3);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const submitUsers = async () => {
    setBusy(true);
    const created = [];
    for (const u of users) {
      if (!u.email.trim()) continue;
      const email = u.email.trim().toLowerCase();
      const tempPassword = _generateTempPassword();
      const name = email.split("@")[0].replace(/[._-]/g, " ");
      try {
        const { data } = await api.post("/admin/users", {
          email,
          name,
          role: u.role,
          password: tempPassword,
          master_company_id: masterId,
        });
        created.push({ email, role: u.role, id: data.id, password: tempPassword,
                        emailSent: false });
      } catch (e) { toast.error(`${email}: ${e.response?.data?.detail || "failed"}`); }
    }
    setCreatedUsers(created);
    setBusy(false);
    setStep(4);
  };

  const sendResetEmail = async (idx) => {
    const target = createdUsers[idx];
    if (!target) return;
    try {
      await api.post("/auth/password-reset/request", { email: target.email });
      setCreatedUsers(createdUsers.map((u, j) => j === idx ? { ...u, emailSent: true } : u));
      toast.success(`Set-password link issued for ${target.email}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const copyOne = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const copyAll = () => {
    const rows = createdUsers
      .map((u) => `${u.email}\t${u.password}\t${u.role}`)
      .join("\n");
    navigator.clipboard.writeText(`Email\tTemp Password\tRole\n${rows}`);
    toast.success(`${createdUsers.length} credentials copied (paste into a secure vault)`);
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-4" data-testid="onboard-wizard">
      <header>
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Administration</div>
        <h1 className="text-2xl font-semibold text-slate-900">Onboard a new tenant</h1>
        <p className="text-[13px] text-slate-500 mt-1">
          A 5-step guided flow: Master → Subsidiaries → Warehouse → Users → Review.
          Every user&apos;s first login automatically triggers the Legal Acceptance gate.
        </p>
      </header>

      {/* Progress bar */}
      <div className="flex items-center gap-1.5">
        {steps.map((s, i) => (
          <div key={s} className="flex-1 flex items-center gap-1.5">
            <div className={"h-1.5 flex-1 rounded-full " +
              (i < step ? "bg-emerald-500" : i === step ? "bg-slate-900" : "bg-slate-200")} />
            <div className={"text-[10px] uppercase tracking-[0.14em] " +
              (i <= step ? "text-slate-900 font-medium" : "text-slate-400")}>{s}</div>
          </div>
        ))}
      </div>

      {step === 0 && (
        <Card title="Step 1 · Master Company" testId="step-master">
          <Field label="Name" value={master.name} onChange={(v) => setMaster({ ...master, name: v })} testId="master-name" />
          <Field label="Code" value={master.code} onChange={(v) => setMaster({ ...master, code: v.toUpperCase() })} testId="master-code" />
          <Field label="Legal name (optional)" value={master.legal_name} onChange={(v) => setMaster({ ...master, legal_name: v })} testId="master-legal" />
          <Field label="Country (ISO-2)" value={master.country} onChange={(v) => setMaster({ ...master, country: v })} testId="master-country" />
          <Field label="Email domain(s) — comma-separated (e.g. acme.com, acme.co.uk)"
                  value={master.domains}
                  onChange={(v) => setMaster({ ...master, domains: v })}
                  testId="master-domains" />
          <div className="text-[11px] text-slate-500 -mt-1 leading-relaxed">
            Users with these domains will see the customer name + logo the moment they
            type their work email — tenant recognition lights up from day one.
          </div>
          <Actions>
            <Primary onClick={submitMaster} busy={busy} testId="master-submit">Create &amp; continue</Primary>
          </Actions>
        </Card>
      )}

      {step === 1 && (
        <Card title="Step 2 · Subsidiaries / Client entities" testId="step-subs">
          {subs.map((s, i) => (
            <div key={i} className="grid grid-cols-3 gap-2 items-end">
              <Field label={`Name ${i + 1}`} value={s.name} onChange={(v) => setSubs(subs.map((x, j) => j === i ? { ...x, name: v } : x))} testId={`sub-name-${i}`} />
              <Field label="Code" value={s.code} onChange={(v) => setSubs(subs.map((x, j) => j === i ? { ...x, code: v.toUpperCase() } : x))} testId={`sub-code-${i}`} />
              <div className="flex gap-1">
                <Field label="Country" value={s.country} onChange={(v) => setSubs(subs.map((x, j) => j === i ? { ...x, country: v } : x))} testId={`sub-country-${i}`} />
                {subs.length > 1 && (
                  <button onClick={() => setSubs(subs.filter((_, j) => j !== i))}
                    className="h-8 w-8 rounded border border-slate-300 grid place-items-center text-slate-500 mb-0.5 shrink-0">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
          <button onClick={() => setSubs([...subs, { name: "", code: "", country: "India" }])}
            data-testid="sub-add"
            className="text-[12px] text-slate-600 hover:text-slate-900 flex items-center gap-1">
            <Plus className="h-3.5 w-3.5" /> Add another
          </button>
          <Actions>
            <Secondary onClick={() => setStep(0)}>Back</Secondary>
            <Primary onClick={submitSubs} busy={busy} testId="sub-submit">Save & continue</Primary>
          </Actions>
        </Card>
      )}

      {step === 2 && (
        <Card title="Step 3 · Primary Warehouse" testId="step-warehouse">
          <Field label="Name" value={warehouse.name} onChange={(v) => setWarehouse({ ...warehouse, name: v })} testId="wh-name" />
          <Field label="Code" value={warehouse.code} onChange={(v) => setWarehouse({ ...warehouse, code: v.toUpperCase() })} testId="wh-code" />
          <Field label="Country" value={warehouse.country} onChange={(v) => setWarehouse({ ...warehouse, country: v })} testId="wh-country" />
          <Actions>
            <Secondary onClick={() => setStep(1)}>Back</Secondary>
            <Primary onClick={submitWarehouse} busy={busy} testId="wh-submit">Save & continue</Primary>
          </Actions>
        </Card>
      )}

      {step === 3 && (
        <Card title="Step 4 · Users (one login per role)" testId="step-users">
          {users.map((u, i) => (
            <div key={i} className="grid grid-cols-[1fr_180px_auto] gap-2 items-end">
              <Field label={`Email ${i + 1}`} value={u.email} onChange={(v) => setUsers(users.map((x, j) => j === i ? { ...x, email: v } : x))} testId={`user-email-${i}`} />
              <div>
                <label className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Role</label>
                <select value={u.role} onChange={(e) => setUsers(users.map((x, j) => j === i ? { ...x, role: e.target.value } : x))}
                  data-testid={`user-role-${i}`}
                  className="w-full h-8 px-2 mt-1 rounded border border-slate-300 bg-white text-[12px]">
                  {CUSTOMER_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              {users.length > 1 && (
                <button onClick={() => setUsers(users.filter((_, j) => j !== i))}
                  className="h-8 w-8 rounded border border-slate-300 grid place-items-center text-slate-500 mb-0.5">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
          <button onClick={() => setUsers([...users, { email: "", role: "customer_it_admin" }])}
            data-testid="user-add"
            className="text-[12px] text-slate-600 hover:text-slate-900 flex items-center gap-1">
            <Plus className="h-3.5 w-3.5" /> Add another user
          </button>
          <Actions>
            <Secondary onClick={() => setStep(2)}>Back</Secondary>
            <Primary onClick={submitUsers} busy={busy} testId="user-submit">Save & continue</Primary>
          </Actions>
        </Card>
      )}

      {step === 4 && (
        <Card title="Step 5 · Review · Onboarding complete" testId="step-review">
          <div className="space-y-3">
            <Row label="Master Company" value={`${master.name} (${master.code})`} ok />
            <Row label="Subsidiaries" value={`${subIds.length} created`} ok={subIds.length > 0} />
            <Row label="Warehouse" value={`${warehouse.name} (${warehouse.code})`} ok={!!warehouseId} />
            <Row label="Users" value={`${createdUsers.length} created`} ok={createdUsers.length > 0} />
          </div>

          {createdUsers.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="credentials-table">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-slate-500">
                  <KeyRound className="h-3.5 w-3.5" /> Temporary credentials
                </div>
                <button onClick={copyAll} data-testid="creds-copy-all"
                  className="text-[11px] text-slate-600 hover:text-slate-900 flex items-center gap-1">
                  <Copy className="h-3 w-3" /> Copy all
                </button>
              </div>
              <div className="rounded border border-amber-200 bg-amber-50 p-2.5 text-[11px] text-amber-900 leading-relaxed">
                Share each password once via a secure channel. Users will be
                prompted to change it on first login, right after signing the
                Legal Acceptance gate. Or click <b>Email set-password link</b>
                to skip sharing passwords entirely.
              </div>
              <div className="rounded border border-slate-200 overflow-hidden">
                <table className="w-full text-[12px]">
                  <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-[0.14em]">
                    <tr>
                      <th className="px-3 py-2 text-left">Email</th>
                      <th className="px-3 py-2 text-left">Role</th>
                      <th className="px-3 py-2 text-left">Temp password</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {createdUsers.map((u, i) => (
                      <tr key={u.id || u.email} className="border-t border-slate-100">
                        <td className="px-3 py-2 text-slate-900">{u.email}</td>
                        <td className="px-3 py-2 text-slate-600">{u.role}</td>
                        <td className="px-3 py-2 font-mono text-slate-800">
                          <span className="mr-2">{u.password}</span>
                          <button onClick={() => copyOne(u.password, "Password")}
                            data-testid={`creds-copy-pw-${i}`}
                            className="text-slate-400 hover:text-slate-900 inline-flex">
                            <Copy className="h-3 w-3" />
                          </button>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => sendResetEmail(i)} disabled={u.emailSent}
                            data-testid={`creds-email-${i}`}
                            className="text-[11px] text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 disabled:text-emerald-600 disabled:cursor-default">
                            <Mail className="h-3 w-3" />
                            {u.emailSent ? "Sent" : "Email set-password link"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="mt-4 rounded border border-emerald-200 bg-emerald-50 p-3 flex items-start gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-700 mt-0.5 shrink-0" />
            <div className="text-[12px] text-emerald-800 leading-relaxed">
              Every new user will see the blocking Legal Acceptance gate on their
              first login (NDA + DPA for global admin, Portal Access Terms for
              everyone). Signed PDFs are emailed automatically.
            </div>
          </div>

          <Actions>
            <Secondary onClick={() => navigate("/admin/legal")}>Open Legal Admin</Secondary>
            <Primary onClick={() => navigate("/")} testId="review-done">Done — go to dashboard</Primary>
          </Actions>
        </Card>
      )}
    </div>
  );
}

/** Generate a memorable-but-strong temp password: 3 blocks × 4 chars, no
 *  confusable glyphs (0/O/1/l/I).  ~62^12 entropy — safe for a one-time
 *  handoff that will be rotated on first login.
 */
function _generateTempPassword() {
  const alphabet = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789";
  const rnd = new Uint32Array(12);
  (window.crypto || window.msCrypto).getRandomValues(rnd);
  const chars = Array.from(rnd, (n) => alphabet[n % alphabet.length]);
  return `${chars.slice(0,4).join("")}-${chars.slice(4,8).join("")}-${chars.slice(8,12).join("")}`;
}

function Card({ title, children, testId }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-5 space-y-3" data-testid={testId}>
      <h2 className="text-[14px] font-semibold text-slate-900">{title}</h2>
      {children}
    </div>
  );
}
function Field({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="w-full h-8 px-2 mt-1 rounded border border-slate-300 bg-white text-[12px] focus:outline-none focus:border-slate-900" />
    </div>
  );
}
function Actions({ children }) {
  return <div className="flex items-center justify-end gap-2 pt-2">{children}</div>;
}
function Primary({ children, onClick, busy, testId }) {
  return (
    <button onClick={onClick} disabled={busy} data-testid={testId}
      className="h-8 px-4 rounded bg-slate-900 text-white text-[12px] font-medium disabled:opacity-50 hover:bg-slate-800 flex items-center gap-1.5">
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />}
      {children}
    </button>
  );
}
function Secondary({ children, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className="h-8 px-3 rounded border border-slate-300 text-[12px] text-slate-600 hover:bg-slate-50 flex items-center gap-1.5">
      <ArrowLeft className="h-3.5 w-3.5" /> {children}
    </button>
  );
}
function Row({ label, value, ok }) {
  return (
    <div className="flex items-center gap-2 text-[12px]">
      <div className={"h-4 w-4 rounded-full grid place-items-center " + (ok ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-400")}>
        {ok && <Check className="h-3 w-3" />}
      </div>
      <div className="text-slate-500 w-32">{label}</div>
      <div className="text-slate-900 font-medium">{value}</div>
    </div>
  );
}
